import type { KeyboardEvent } from 'react';
import type { EmployerPendingReviewItem } from '@fdh/shared';
import { useLanguage } from '../../contexts/LanguageContext';
import { PendingReviewSubmittedDocumentIcon } from './PendingReviewSubmittedDocumentIcon';
import { PendingReviewWarningClipboardWatermark } from './PendingReviewWarningClipboardWatermark';

type EmployerPendingReviewDashboardCardProps = {
  items: EmployerPendingReviewItem[];
  pendingTotal: number;
  onRowClick: (taskInstanceId: string) => void;
  onViewAll: () => void;
};

export const EmployerPendingReviewDashboardCard = ({
  items,
  pendingTotal,
  onRowClick,
  onViewAll,
}: EmployerPendingReviewDashboardCardProps) => {
  const { t } = useLanguage();

  const handleRowKeyDown = (event: KeyboardEvent<HTMLDivElement>, taskInstanceId: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRowClick(taskInstanceId);
    }
  };

  const handleViewAllKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onViewAll();
    }
  };

  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-amber-200/70 bg-gradient-to-br from-amber-50/90 via-white to-orange-50/80 p-4 shadow-sm dark:border-amber-800/50 dark:bg-gradient-to-br dark:from-gray-900 dark:via-amber-950/70 dark:to-gray-950"
      aria-labelledby="dash-pending-heading"
    >
      <div
        className="pointer-events-none absolute -right-8 -top-6 opacity-[0.35] dark:opacity-60"
        aria-hidden
      >
        <PendingReviewWarningClipboardWatermark className="h-[200px] w-[200px] max-w-none" />
      </div>

      <div className="relative z-10">
        <div className="mb-3 flex items-center gap-2">
          <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-300">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
              />
            </svg>
          </span>
          <h2
            id="dash-pending-heading"
            className="text-base font-semibold text-gray-900 dark:text-gray-100"
          >
            {t('employerDashboard.pendingTitle')}
          </h2>
          {pendingTotal > 0 ? (
            <span className="inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-orange-500 px-1.5 text-xs font-bold text-white">
              {pendingTotal > 99 ? '99+' : pendingTotal}
            </span>
          ) : null}
        </div>

        {items.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('employerDashboard.pendingEmpty')}</p>
        ) : (
          <ul className="flex flex-col gap-2" role="list">
            {items.map((row) => (
              <li key={row.taskInstanceId}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => onRowClick(row.taskInstanceId)}
                  onKeyDown={(event) => handleRowKeyDown(event, row.taskInstanceId)}
                  aria-label={t('task.pendingReview.reviewCta', { title: row.title })}
                  className="flex cursor-pointer items-center gap-3 rounded-xl border border-amber-100/80 bg-white/75 p-3 shadow-sm backdrop-blur-[2px] transition-colors hover:bg-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700/60 dark:bg-gray-800/55 dark:hover:bg-gray-800/75"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center">
                    <PendingReviewSubmittedDocumentIcon className="h-10 w-10" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px] font-semibold text-gray-900 dark:text-gray-100">
                      {row.title}
                    </p>
                    <p className="truncate text-[13px] font-medium text-gray-500 dark:text-gray-400">
                      {t('employerDashboard.submittedAtLine', { at: row.submittedAt })}
                    </p>
                  </div>
                  <svg
                    className="h-6 w-6 shrink-0 text-gray-400 dark:text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </li>
            ))}
          </ul>
        )}

        {pendingTotal > 0 ? (
          <div className="mt-4 border-t border-amber-200/70 pt-3 text-center dark:border-gray-700/60">
            <button
              type="button"
              tabIndex={0}
              onClick={() => void onViewAll()}
              onKeyDown={handleViewAllKeyDown}
              className="text-sm font-semibold text-amber-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:text-amber-400"
            >
              {t('employerDashboard.viewAllPending')}
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
};
